import { formatDateRange } from '../../../utils/formats/dates';
import { DayFormatOption } from '../../../utils/types/date';
import type { BaseDate, DateRangeOptions, FormattedDateRangeObject } from './date-range-view-types';

export const getFormattedDateRangeFunction = ({
	timezone,
	locale,
	dateStyle = 'medium',
	timeStyle = 'short',
	dayStyle = DayFormatOption.Short,
}: {
	timezone?: string;
	locale?: string;
	dateStyle?: Intl.DateTimeFormatOptions['dateStyle'];
	timeStyle?: Intl.DateTimeFormatOptions['timeStyle'];
	dayStyle?: DayFormatOption;
}) => {
	const dateFormatter = new Intl.DateTimeFormat(locale, {
		dateStyle,
		timeZone: timezone,
	});

	const timeFormatter = new Intl.DateTimeFormat(locale, {
		timeStyle,
		timeZone: timezone,
	});

	const dayFormatter = new Intl.DateTimeFormat(locale, {
		weekday: dayStyle,
		timeZone: timezone,
	});

	return (
		startDate: BaseDate,
		endDate: BaseDate,
		details?: string[],
		options?: DateRangeOptions
	): FormattedDateRangeObject => {
		const fallback: FormattedDateRangeObject = {
			formattedDateRange: '',
			formattedTimeRange: '',
			formattedExtraDetails: [],
		};

		try {
			if (!startDate || !endDate) return fallback;

			const startDateObj = new Date(startDate);
			const endDateObj = new Date(endDate);

			if (isNaN(startDateObj.getTime()) || isNaN(endDateObj.getTime())) {
				return fallback;
			}

			const formattedFirstDate = dateFormatter.format(startDateObj);
			const formattedSecondDate = dateFormatter.format(endDateObj);

			const formattedDateRange = formatDateRange(formattedFirstDate, formattedSecondDate);

			const daysRange = options?.hideDays ? '' : getDaysRange(dayFormatter, startDateObj, endDateObj);

			let formattedTimeRange = '';

			if (!options?.hideTime) {
				const formattedFirstTime = timeFormatter.format(startDateObj);
				const formattedSecondTime = timeFormatter.format(endDateObj);
				formattedTimeRange = formatDateRange(formattedFirstTime, formattedSecondTime);
			}

			if (daysRange) {
				formattedTimeRange = formattedTimeRange ? `${daysRange} ${formattedTimeRange}` : daysRange;
			}

			const formattedExtraDetails = [];

			if (details?.length) {
				formattedExtraDetails.push(...details);
			}

			return {
				formattedDateRange,
				formattedTimeRange,
				formattedExtraDetails,
			};
		} catch (error) {
			console.error('Error formatting datetime:', error);
			return fallback;
		}
	};
};

const getDaysRange = (dayFormatter: Intl.DateTimeFormat, startDate: Date, endDate: Date): string => {
	const startDay = dayFormatter.format(startDate);
	const endDay = dayFormatter.format(endDate);
	return formatDateRange(startDay, endDay);
};
